import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Basic from "../../container/layouts/Basic";

// COMPTA-173: public landing page for the link sent in the confirmation email.
// Reads ?token=... from the URL, POSTs to the Express proxy /auth/email-confirmation/confirm,
// renders one of: loading / success / expired / invalid / error.
// A resend form is offered on the expired branch.

const STATUS_LOADING = "loading";
const STATUS_OK = "ok";
const STATUS_EXPIRED = "expired";
const STATUS_INVALID = "invalid";
const STATUS_ERROR = "error";

function readToken() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("token") || "";
  } catch (e) {
    return "";
  }
}

function Card({ children }) {
  return (
    <div
      style={{
        maxWidth: 480,
        margin: "40px auto",
        padding: 32,
        background: "#fff",
        borderRadius: 12,
        boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
        textAlign: "center",
        fontFamily: "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif"
      }}
    >
      {children}
    </div>
  );
}

function ResendForm() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || busy) return;
    setBusy(true);
    setRateLimited(false);
    try {
      const res = await axios.post("/auth/email-confirmation/resend", { email });
      if (res.status === 429) {
        setRateLimited(true);
      } else {
        setSent(true);
      }
    } catch (err) {
      if (err.response && err.response.status === 429) {
        setRateLimited(true);
      } else {
        // Anti-enumeration: the backend returns 200 even on unknown email.
        setSent(true);
      }
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return <p style={{ color: "#16a34a", marginTop: 16 }}>Si cette adresse existe, un nouveau lien vient d&apos;etre envoye.</p>;
  }
  return (
    <form onSubmit={submit} style={{ marginTop: 24, textAlign: "left" }}>
      <label htmlFor="resend-email" style={{ display: "block", fontWeight: 600, marginBottom: 8 }}>
        Renvoyer un lien de confirmation
      </label>
      <input
        id="resend-email"
        type="email"
        required
        placeholder="votre@email.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 14 }}
      />
      {rateLimited && (
        <p style={{ color: "#d97706", marginTop: 8, fontSize: 13 }}>
          Patientez quelques minutes avant de redemander un envoi.
        </p>
      )}
      <button
        type="submit"
        disabled={busy}
        style={{
          marginTop: 12,
          width: "100%",
          padding: "10px 16px",
          background: "#2563eb",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          fontWeight: 600,
          cursor: busy ? "not-allowed" : "pointer",
          opacity: busy ? 0.6 : 1
        }}
      >
        {busy ? "Envoi..." : "Renvoyer"}
      </button>
    </form>
  );
}

export default function EmailConfirmationPage() {
  const [status, setStatus] = useState(STATUS_LOADING);
  const token = readToken();

  useEffect(() => {
    if (!token) {
      setStatus(STATUS_INVALID);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post("/auth/email-confirmation/confirm", { token });
        if (cancelled) return;
        const payload = res && res.data;
        const next = (payload && payload.status) || STATUS_ERROR;
        setStatus(next);
      } catch (err) {
        if (cancelled) return;
        const payload = err && err.response && err.response.data;
        setStatus((payload && payload.status) || STATUS_ERROR);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  let content;
  if (status === STATUS_LOADING) {
    content = (
      <>
        <h1 style={{ fontSize: 22, marginBottom: 12 }}>Verification en cours...</h1>
        <p style={{ color: "#4b5563" }}>Confirmation de votre adresse email.</p>
      </>
    );
  } else if (status === STATUS_OK) {
    content = (
      <>
        <div style={{ fontSize: 48, color: "#16a34a", marginBottom: 8 }}>&#10004;</div>
        <h1 style={{ fontSize: 22, marginBottom: 12 }}>Email confirme</h1>
        <p style={{ color: "#4b5563", marginBottom: 24 }}>
          Votre adresse email a bien ete verifiee. Vous pouvez vous connecter.
        </p>
        <Link
          to="/login"
          style={{
            display: "inline-block",
            padding: "12px 24px",
            background: "#2563eb",
            color: "#fff",
            textDecoration: "none",
            borderRadius: 8,
            fontWeight: 600
          }}
        >
          Aller a l&apos;application
        </Link>
      </>
    );
  } else if (status === STATUS_EXPIRED) {
    content = (
      <>
        <div style={{ fontSize: 48, color: "#d97706", marginBottom: 8 }}>&#9888;</div>
        <h1 style={{ fontSize: 22, marginBottom: 12 }}>Lien expire</h1>
        <p style={{ color: "#4b5563" }}>
          Ce lien de confirmation n&apos;est plus valide. Demandez-en un nouveau ci-dessous.
        </p>
        <ResendForm />
      </>
    );
  } else if (status === STATUS_INVALID) {
    content = (
      <>
        <div style={{ fontSize: 48, color: "#dc2626", marginBottom: 8 }}>&#10006;</div>
        <h1 style={{ fontSize: 22, marginBottom: 12 }}>Lien invalide</h1>
        <p style={{ color: "#4b5563" }}>
          Ce lien est inconnu ou a deja ete utilise. Si vous en avez recu plusieurs, utilisez le plus recent.
        </p>
      </>
    );
  } else {
    content = (
      <>
        <div style={{ fontSize: 48, color: "#dc2626", marginBottom: 8 }}>!</div>
        <h1 style={{ fontSize: 22, marginBottom: 12 }}>Erreur inattendue</h1>
        <p style={{ color: "#4b5563" }}>
          Quelque chose s&apos;est mal passe. Reessayez plus tard ou demandez un nouveau lien.
        </p>
        <ResendForm />
      </>
    );
  }

  return (
    <Basic page="email-confirmation" noBanner>
      <Card>{content}</Card>
    </Basic>
  );
}
